import React, { FC } from 'react'

import { IS_EMBEDDED } from '@sketch/constants'
import { ShareWithoutVersion } from 'modules/versioning'
import {
  useForDesktop,
  useForTablet,
  Navbar,
  ModalRestrictor,
} from '@sketch/components'

import HeaderNavigation from '../HeaderNavigation'
import HeaderSidebarToggle from '../HeaderSidebarToggle'
import { EditInSketchButton, OpenTemplateButton } from '../EditInSketchButton'

import { VersionStatusButton } from './VersionStatusButton'
import DocumentSettingsDropdown from './DocumentSettingsDropdown'
import { NavbarItem } from './DocumentHeader.styles'
import DocumentNavbar from '../DocumentNavbar'
import { DocumentSidebarLayoutExtraProps } from '../DocumentSidebarLayout'

type DocumentHeaderProps = {
  share: ShareWithoutVersion
  isViewingLatestVersion: boolean
  isViewingStarredVersion: boolean
  hasPendingPatches: boolean
  userCanOpenInApp: boolean
} & Pick<
  DocumentSidebarLayoutExtraProps,
  'setSidebarLeftOpen' | 'toggleSidebarRight' | 'isSidebarRightOpen'
>

export const DocumentHeader: FC<DocumentHeaderProps> = ({
  share,
  toggleSidebarRight,
  isSidebarRightOpen,
  userCanOpenInApp,
  isViewingLatestVersion,
  isViewingStarredVersion,
  hasPendingPatches,
}) => {
  const isDesktop = useForDesktop()
  const isTabletAndBigger = useForTablet()

  const { type } = share

  const canOpenInApp = userCanOpenInApp && isDesktop && !IS_EMBEDDED
  const showVersionStatusButton = isTabletAndBigger && !IS_EMBEDDED
  const isTemplate = type === 'TEMPLATE'

  return (
    <Navbar>
      <Navbar.Section>
        <HeaderNavigation share={share} />
      </Navbar.Section>

      <Navbar.FixedWidthSection align="end">
        {isTabletAndBigger && (
          <NavbarItem>
            <DocumentNavbar />
          </NavbarItem>
        )}

        {canOpenInApp && isTemplate && (
          <NavbarItem>
            <OpenTemplateButton
              shareId={share.identifier}
              disabled={!isViewingLatestVersion}
            />
          </NavbarItem>
        )}

        {canOpenInApp && !isTemplate && (
          <NavbarItem>
            <EditInSketchButton
              shareId={share.identifier}
              disabled={!isViewingLatestVersion}
            />
          </NavbarItem>
        )}

        {showVersionStatusButton && (
          <NavbarItem>
            <VersionStatusButton
              shareID={share.identifier}
              isViewingLatestVersion={isViewingLatestVersion}
              isViewingStarredVersion={isViewingStarredVersion}
              userCanEdit={userCanOpenInApp}
              hasPendingPatches={hasPendingPatches}
            />
          </NavbarItem>
        )}

        <NavbarItem>
          <ModalRestrictor>
            <DocumentSettingsDropdown />
          </ModalRestrictor>
        </NavbarItem>

        <HeaderSidebarToggle
          toggleSidebar={toggleSidebarRight}
          isSidebarOpen={isSidebarRightOpen}
        />
      </Navbar.FixedWidthSection>
    </Navbar>
  )
}

export default DocumentHeader
